import { useEffect, useState } from "react";

type PlayerState = "idle" | "loading" | "playing";

interface PlayerProps {
  cover: string;
  title: string;
  duration: string;
  onEnlarge?: () => void;
}

export default function Player({ cover, title, duration, onEnlarge }: PlayerProps) {
  const [state, setState] = useState<PlayerState>("idle");
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (state !== "loading") return;
    const timer = window.setTimeout(() => {
      setState("playing");
      setPaused(false);
    }, 1600);
    return () => window.clearTimeout(timer);
  }, [state]);

  const startPlayback = () => {
    if (state === "loading") return;
    if (state === "playing") {
      setPaused((current) => !current);
      return;
    }
    setState("loading");
  };

  return (
    <div className="overflow-hidden rounded-lg border border-background-200 bg-primary-950">
      <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
        <img
          src={cover}
          alt={`${title} 播放画面`}
          title={`${title} 视频播放演示`}
          className={`h-full w-full object-cover object-top transition-all duration-700 ${
            state === "loading" ? "scale-105 blur-sm brightness-50" : ""
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-primary-950/20 to-primary-950/30" />

        {state === "idle" && (
          <>
            <button
              type="button"
              onClick={startPlayback}
              aria-label="播放视频演示"
              className="group absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-4"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-primary-950 transition-transform duration-300 group-hover:scale-110 md:h-[72px] md:w-[72px]">
                <i className="ri-play-fill text-3xl md:text-4xl" />
              </span>
              <span className="rounded-full bg-primary-950/60 px-3 py-1 text-xs text-background-100 backdrop-blur-sm">
                点击播放（演示）
              </span>
            </button>

            {onEnlarge && (
              <button
                type="button"
                onClick={onEnlarge}
                aria-label="查看封面大图"
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-md border border-background-50/30 bg-primary-950/50 text-background-50 backdrop-blur-sm transition-colors hover:bg-primary-950/75"
              >
                <i className="ri-expand-diagonal-line text-base" />
              </button>
            )}

            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 px-4 py-3">
              <p className="line-clamp-2-custom text-[13px] font-medium text-background-50">{title}</p>
              <span className="shrink-0 font-label text-[11px] tracking-widest text-background-200">{duration}</span>
            </div>
          </>
        )}

        {state === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <span className="anim-spin-slow flex h-12 w-12 items-center justify-center rounded-full border-2 border-background-50/25 border-t-accent-500" />
            <div className="text-center">
              <p className="text-sm font-medium text-background-50">正在加载播放资源…</p>
              <p className="mt-1 font-label text-[11px] tracking-widest text-background-200">MOCK STREAM · 演示加载</p>
            </div>
          </div>
        )}

        {state === "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur-sm">
              <i className={paused ? "ri-pause-fill text-2xl" : "ri-play-fill text-2xl"} />
            </span>
            <p className="mt-4 text-center text-base font-semibold text-background-50">视频播放演示</p>
            <p className="mt-1.5 max-w-sm text-center text-xs leading-relaxed text-background-200">
              当前为前端模拟播放界面，真实播放待接入接口。
            </p>
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-accent-500/60 bg-accent-500/15 px-3 py-1 font-label text-[10px] tracking-[0.2em] text-accent-300">
              <i className="ri-live-line text-xs" />
              DEMO MODE
            </span>
          </div>
        )}

        {state === "playing" && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary-950/95 to-transparent px-3 pb-3 pt-8 md:px-4">
            <div className="h-1 w-full overflow-hidden rounded-full bg-background-50/25">
              <div
                className={`h-full rounded-full bg-accent-500 ${paused ? "" : "anim-progress"}`}
                style={{ animationPlayState: paused ? "paused" : "running", width: paused ? "38%" : undefined }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={startPlayback}
                  aria-label={paused ? "继续播放" : "暂停播放"}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/25"
                >
                  <i className={paused ? "ri-play-fill text-sm" : "ri-pause-fill text-sm"} />
                </button>
                <span className="font-label text-[11px] tracking-widest text-background-200">
                  {paused ? "已暂停" : "演示播放中"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-label text-[11px] tracking-widest text-background-200">{duration}</span>
                {onEnlarge && (
                  <button
                    type="button"
                    onClick={onEnlarge}
                    aria-label="查看封面大图"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-background-200 transition-colors hover:text-background-50"
                  >
                    <i className="ri-expand-diagonal-line text-sm" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-primary-800 bg-primary-900 px-4 py-3">
        <p className="flex items-center gap-2 text-xs text-background-200">
          <i className="ri-information-line text-sm text-accent-400" />
          播放功能为前端演示，真实播放待接入接口
        </p>
        <button
          type="button"
          onClick={() => {
            setState("idle");
            setPaused(false);
          }}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-primary-700 px-2.5 py-1 text-[11px] text-background-100 transition-colors hover:border-accent-500 hover:text-accent-300"
        >
          <i className="ri-restart-line text-xs" />
          重置演示
        </button>
      </div>
    </div>
  );
}